import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { AppShellRootComponent, type ShellNavItem } from '@sbe-spinoff-client/app-shell';
import {
  StaffAuthzService,
  StaffOidcAuthService,
  StaffProfileMenuComponent,
  injectStaffOidcBootLoading,
} from '@sbe-spinoff-client/staff-oidc-auth';
import { appRoutes } from './app.routes';
import { environment } from './environments/environment';
import { SmsStoreApiService } from './features/sms-store/data-access/sms-store-api.service';

@Component({
  selector: 'app-root',
  imports: [AppShellRootComponent, StaffProfileMenuComponent],
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- Angular 22 스키매틱이 기존 동작 보존을 위해 삽입(Nx 23 마이그레이션, 2026-08-25) — OnPush 전환은 별도 후속 작업
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <sbe-app-shell-root
      header="toolbar"
      title="SMS 어드민"
      [showBootLoading]="showBootLoading()"
      [loggedIn]="isAuthenticated()"
      [navItems]="navItems()"
    >
      <!--
        슬롯 하나당 @if 하나씩 — 같은 @if 블록에 root 노드가 여러 개면(같은 슬롯을
        가리키더라도) Angular가 어느 쪽도 투영하지 못한다(NG8011). ng-container로
        묶어 root 노드를 하나로 만든다.
      -->
      @if (isAuthenticated()) {
        <app-staff-profile-menu
          shellToolbarEnd
          (logoutRequested)="logout()"
        />
      }
      @if (isAuthenticated() && authz.denial(); as denial) {
        <p shellBanner class="text-red-600 text-sm px-6 pt-2 dark:text-red-400" role="alert">
          {{ denial.message }}
          <button type="button" class="underline ml-2" (click)="authz.clearDenial()">닫기</button>
        </p>
      }
      @if (isAuthenticated() && storeError()) {
        <p shellBanner class="text-red-600 text-sm px-6 pt-2 dark:text-red-400">
          {{ storeError() }}
        </p>
      }
    </sbe-app-shell-root>
  `,
})
export class App {
  private readonly auth = inject(StaffOidcAuthService);
  protected readonly authz = inject(StaffAuthzService);
  private readonly smsStore = inject(SmsStoreApiService);

  /**
   * `dashboard`/`orgs/:orgId` 등 guard 보호 라우트로 콜드 진입/새로고침 시, `staffAuthGuard`가
   * `loadMe()`를 기다리는 동안 콘텐츠 영역에 로딩 스피너를 표시한다 — 그 전엔 이 앱이
   * `AppShellRootComponent`를 쓰지 않아 빈 화면으로 남았다(2026-08-13).
   */
  protected readonly showBootLoading = injectStaffOidcBootLoading(appRoutes);

  /**
   * 헤더는 모든 라우트에서 항상 보인다(2026-08-12 사용자 확인) — 라우트별로 감추지
   * 않는다. 나머지 nav/로그아웃/에러 배너는 여전히 인증 상태로만 감춘다: `me`는
   * StaffOidcAuthService 의 표시용 캐시 signal이라(로그아웃 시 null로 리셋) 순수
   * UI 토글에만 쓰고, 실제 인가 경계는 여전히 서버·가드가 담당한다.
   */
  protected readonly isAuthenticated = computed(() => !!this.auth.me());

  /**
   * 좌측 사이드바 nav 항목 — 헤더 toolbar에 있던 발신번호/발송 이력/단체 링크 탭을
   * 여기로 옮겼다(2026-08-28, 사용자 지시). 항목마다 leading icon을 고르는 관행은
   * 새 메뉴를 추가할 때마다 부담이라는 지적으로 없앴다(레이블 텍스트만). "문자
   * 스토어"는 이 앱 내부 라우트가 아니라 팝업으로 외부 서비스에 진입하는 액션이라
   * `routerLink` 대신 `action`으로 준다(`ShellNavItem`이 2026-08-28에 지원 추가 —
   * 이전엔 `shellSidenavContent`에 별도 투영해 목록 자체의 padding/gap과 어긋나던
   * 걸 매번 보정해야 했다). 새 탭/팝업으로 열리는 항목이라 `trailingIcon`으로
   * open_in_new를 붙인다.
   */
  protected readonly navItems = computed<readonly ShellNavItem[]>(() =>
    this.isAuthenticated()
      ? [
          { label: '발신번호', routerLink: '/originators' },
          { label: '발송 이력', routerLink: '/messages' },
          { label: '인증 이력', routerLink: '/two-factor-auth-history' },
          { label: '단체 링크', routerLink: '/org-provider-link' },
          ...(this.authz.has('audit-log.view') ? [{ label: '접속기록', routerLink: '/audit-logs' }] : []),
          ...(this.authz.canManageStaff()
            ? [
                { label: 'staff 권한', routerLink: '/staff-members' },
                { label: 'staff 초대', routerLink: '/staff-invitations' },
              ]
            : []),
          {
            label: '문자 스토어',
            action: () => this.openSmsStore(),
            trailingIcon: 'open_in_new',
            disabled: this.openingStore() || !this.authz.has('store.enter'),
            tooltip: this.authz.requiredMessage('store.enter'),
          },
        ]
      : [],
  );

  readonly openingStore = signal(false);
  readonly storeError = signal<string | null>(null);

  async logout(): Promise<void> {
    await this.auth.logout();
    window.location.href = '/signed-out';
  }

  /**
   * 팝업 차단 회피 — 클릭 핸들러 안에서 동기적으로 빈 탭을 먼저 연 뒤 토큰 발급 후
   * 이동시킨다. 빈 탭이 애초에 안 열렸다면(팝업 차단) enterToken() API 호출 자체가
   * 낭비고, 그 뒤 비동기 콜백 안에서 다시 window.open()을 시도해도 브라우저 정책상
   * 똑같이 막혀 사용자에게 아무 피드백 없이 조용히 실패한다 — 그래서 동기적으로
   * 먼저 확인해 막혔으면 즉시 안내하고 끝낸다. 성공 시 openingStore를 finally에서
   * 풀지 않는다 — 리다이렉트 중 버튼이 계속 disabled로 남아야 중복 클릭(중복 제출)을
   * 막는다(Gemini 코드 리뷰 지적, PR #1669).
   */
  async openSmsStore(): Promise<void> {
    this.storeError.set(null);
    const popup = window.open('', '_blank');
    if (!popup) {
      this.storeError.set(
        '팝업 차단이 활성화되어 있습니다. 브라우저 설정에서 팝업을 허용해주세요.',
      );
      return;
    }

    this.openingStore.set(true);
    try {
      const { accessToken } = await this.smsStore.enterToken();
      const url = new URL('/auth/enter', environment.commerceWebBaseUrl);
      url.searchParams.set('token', accessToken);
      popup.location.href = url.toString();
    } catch {
      popup.close();
      this.storeError.set(
        '문자 스토어 진입에 실패했습니다. 잠시 후 다시 시도해주세요.',
      );
      this.openingStore.set(false);
    }
  }
}
